import React, { useState, useCallback } from 'react';
import investmentJson from './investmentData.json';
import investmentMetric from './investmentMetric.json';
import PageLayout from './PageLayout';
import './styles/clean.css';
import './styles/investmentCalculator.css';

/* TODO List unordered
- implement ups and downs within range in market related investments to calculate return by varying rate each year
- introduce high water mark in AIF
- add better (correct) interest and tax numbers for each investment
*/

const calculatePerformanceFee = (returns, performanceFee, performanceTarget) => {
  if (returns > performanceTarget && performanceFee) {
    const excessReturn = returns - performanceTarget;
    return excessReturn * performanceFee;
  }
  return 0;
};

const calculateReturns = (principal, years, data, taxSlab, applyExemptions) => {
  if (!data || typeof data.Returns !== 'number') return null;

  const returns = data.Returns;
  const chargesRate = data.Charges || 0;
  const performanceFee = data.performanceFee || 0;
  const performanceTarget = data.performanceTarget || 0;
  const rentalYield = data.RentalYield || 0;
  const maintenanceRate = data.MaintenanceRate || 0;
  const depreciationRate = data.DepreciationRate || 0;

  let initialPrincipal = principal;
  if (applyExemptions) {
    if (data.TaxExemptContribution) {
      const exemptLimit = 150000;
      initialPrincipal = (principal >= exemptLimit)
        ? (principal - exemptLimit) * (1 - taxSlab / 100) + exemptLimit
        : principal;
    } else {
      initialPrincipal = principal * (1 - taxSlab / 100);
    }
  }

  let amount = initialPrincipal;
  let accumulatedCash = 0;
  let totalCharges = 0;
  let totalPerformanceFees = 0;

  for (let i = 0; i < years; i++) {
    const yearlyAppreciation = amount * returns;
    amount += yearlyAppreciation;
    const yearlyDepreciation = amount * depreciationRate;
    amount -= yearlyDepreciation;
    const yearlyRent = amount * rentalYield;
    const yearlyMaintenance = amount * maintenanceRate;
    const netYearlySlabIncome = yearlyRent - yearlyMaintenance;
    accumulatedCash += netYearlySlabIncome;
    const yearlyPerformanceFee = calculatePerformanceFee(returns, performanceFee, performanceTarget) * yearlyAppreciation;
    totalPerformanceFees += yearlyPerformanceFee;
    amount -= yearlyPerformanceFee;
    const yearlyCharge = amount * chargesRate;
    totalCharges += yearlyCharge;
    amount -= yearlyCharge;
  }

  const interest = amount - initialPrincipal;
  let tax = 0;

  const LTCG_EXEMPTION = 125000;

  const applyLtcg = (gain, ltcgRate) => {
    if (ltcgRate === "slab") return Math.max(gain, 0) * (taxSlab / 100);
    return Math.max(gain - LTCG_EXEMPTION, 0) * ltcgRate;
  };

  if (!data.TaxExemptInterest && !data.TaxExemptReturns) {
    if (data.StcgLtcgMix) {
      const ltcgInterest = interest * 0.65;
      const stcgInterest = interest * 0.35;
      if (years > 1) {
        tax += applyLtcg(ltcgInterest, data.LTCG);
        tax += stcgInterest * (data.STCG === "slab" ? taxSlab / 100 : data.STCG);
      } else {
        tax = interest * (data.STCG === "slab" ? taxSlab / 100 : data.STCG);
      }
    } else {
      if (data.LTCG && years > 1) {
        tax = applyLtcg(interest, data.LTCG);
      } else if (data.STCG) {
        tax = interest * (data.STCG === "slab" ? taxSlab / 100 : data.STCG);
      } else if (data.TaxableInterest) {
        tax = interest * (taxSlab / 100);
      } else if (data.Taxation === "Pass-through" || data.Taxation === "As per underlying securities") {
        tax = interest * (taxSlab / 100);
      } else if (data.Taxation === "Fund level") {
        tax = interest * (data.taxRate || 0.30);
      } else {
        tax = interest * (taxSlab / 100);
      }
    }
  }

  const finalValue = amount + accumulatedCash - tax;
  return {
    interest: parseFloat(interest.toFixed(2)),
    tax: parseFloat(tax.toFixed(2)),
    charges: parseFloat(totalCharges.toFixed(2)),
    performanceFees: parseFloat(totalPerformanceFees.toFixed(2)),
    finalValue: parseFloat(finalValue.toFixed(2)),
    assetValue: parseFloat(amount.toFixed(2)),
    accumulatedCash: parseFloat(accumulatedCash.toFixed(2)),
    interestRate: returns,
    chargesRate,
    stcgLtcgMix: data.StcgLtcgMix,
    performanceFee,
    performanceTarget,
    rentalYield,
    maintenanceRate,
    depreciationRate,
  };
};

const calculateSIPReturns = (principal, years, sipDuration, data, taxSlab, applyExemptions) => {
  if (!data || typeof data.Returns !== 'number' || !data.SIPEligible) return null;

  let totalFinalValue = 0;
  let totalInterest = 0;
  let totalTax = 0;
  let totalCharges = 0;
  let totalPerformanceFees = 0;
  let totalAssetValue = 0;
  let totalAccumulatedCash = 0;
  let totalInvested = 0;
  let totalLTCGInterest = 0;
  let totalSTCGInterest = 0;

  for (let i = 0; i < sipDuration; i++) {
    const holdingPeriod = years - i;
    const result = calculateReturns(principal, holdingPeriod, data, taxSlab, applyExemptions);
    if (result) {
      totalFinalValue += result.finalValue;
      totalInterest += result.interest;
      totalCharges += result.charges;
      totalPerformanceFees += result.performanceFees;
      totalAssetValue += result.assetValue;
      totalAccumulatedCash += result.accumulatedCash;
      totalInvested += principal;

      if (data.StcgLtcgMix) {
        totalLTCGInterest += result.interest * 0.65;
        totalSTCGInterest += result.interest * 0.35;
      } else {
        if (data.LTCG && holdingPeriod > 1) {
          totalLTCGInterest += result.interest;
        } else if (data.STCG) {
          totalSTCGInterest += result.interest;
        } else if (data.TaxableInterest) {
          totalSTCGInterest += result.interest;
        }
      }
    }
  }

  if (!data.TaxExemptInterest && !data.TaxExemptReturns) {
    const ltcgRate = data.LTCG || 0;
    if (ltcgRate === "slab") {
      totalTax = Math.max(totalLTCGInterest, 0) * (taxSlab / 100);
    } else {
      const taxableLTCGInterest = Math.max(totalLTCGInterest - 125000, 0);
      totalTax = taxableLTCGInterest * ltcgRate;
    }
    if (data.STCG) {
      totalTax += totalSTCGInterest * (data.STCG === "slab" ? taxSlab / 100 : data.STCG);
    } else if (data.TaxableInterest) {
      totalTax += totalSTCGInterest * (taxSlab / 100);
    }
  }

  return {
    interest: parseFloat(totalInterest.toFixed(2)),
    tax: parseFloat(totalTax.toFixed(2)),
    charges: parseFloat(totalCharges.toFixed(2)),
    performanceFees: parseFloat(totalPerformanceFees.toFixed(2)),
    finalValue: parseFloat(totalFinalValue.toFixed(2)),
    assetValue: parseFloat(totalAssetValue.toFixed(2)),
    accumulatedCash: parseFloat(totalAccumulatedCash.toFixed(2)),
    interestRate: data.Returns,
    chargesRate: data.Charges || 0,
    stcgLtcgMix: data.StcgLtcgMix,
    performanceFee: data.performanceFee || 0,
    performanceTarget: data.performanceTarget || 0,
    rentalYield: data.RentalYield || 0,
    maintenanceRate: data.MaintenanceRate || 0,
    depreciationRate: data.DepreciationRate || 0,
    isSIP: true,
    sipDuration,
    totalInvested,
  };
};

const calculateAllReturns = (principal, years, taxSlab, data, applyExemptions, parentKey = '', isSIP = false, sipDuration = 0) => {
  let results = {};
  Object.entries(data).forEach(([key, value]) => {
    const fullKey = parentKey ? `${parentKey} - ${key}` : key;
    if (typeof value === 'object' && value !== null && !('Returns' in value)) {
      const subResults = calculateAllReturns(principal, years, taxSlab, value, applyExemptions, fullKey, isSIP, sipDuration);
      results = { ...results, ...subResults };
    } else {
      let result;
      if (isSIP && sipDuration > 0 && value.SIPEligible) {
        result = calculateSIPReturns(principal, years, sipDuration, value, taxSlab, applyExemptions);
      } else {
        result = calculateReturns(principal, years, value, taxSlab, applyExemptions);
      }
      if (result) results[fullKey] = result;
    }
  });
  return results;
};

const fmt = (n) => '₹' + n.toLocaleString('en-IN');

const SCENARIO_PRESETS = [
  {
    label: 'One-time 10yr',
    description: 'Invest once and compare long-term outcomes.',
    values: { principal: '100000', years: '10', taxSlab: '30', isSIP: false, sipDuration: '', inflationRate: '6' },
  },
  {
    label: 'Yearly SIP 10yr',
    description: 'Invest the same amount every year for 10 years.',
    values: { principal: '100000', years: '10', taxSlab: '30', isSIP: true, sipDuration: '10', inflationRate: '6' },
  },
  {
    label: 'Tax-saving check',
    description: 'Compare 80C-style assumptions from pre-tax income.',
    values: { principal: '150000', years: '5', taxSlab: '30', isSIP: false, sipDuration: '', inflationRate: '6', applyExemptions: true },
  },
];

const getInflationAdjustedValue = (value, years, inflationRate) => {
  if (!Number.isFinite(years) || years <= 0 || !Number.isFinite(inflationRate) || inflationRate <= 0) return value;
  return value / Math.pow(1 + inflationRate / 100, years);
};

const renderTaxationInfo = (data) => {
  if (data.Taxation === "Fund level" && data.taxRate) {
    return `Fund level taxation at ${(data.taxRate * 100).toFixed(2)}%`;
  }
  const taxInfo = [];
  if (data.TaxExemptContribution) taxInfo.push("Contributions tax-exempt (Sec 80C)");
  if (data.TaxExemptInterest || data.TaxExemptReturns) taxInfo.push("Interest/Returns tax-exempt");
  if (data.TaxExemptWithdrawal) taxInfo.push("Withdrawal tax-exempt");
  if (data.TaxExemptCapitalGains) taxInfo.push("Capital gains tax-exempt");
  if (data.TaxExemptDividends) taxInfo.push("Dividends tax-exempt");
  if (data.TaxableInterest) taxInfo.push("Interest taxable at slab rate");
  if (data.TaxableCapitalGains) taxInfo.push("Capital gains taxable");
  if (data.LTCG) {
    const r = typeof data.LTCG === 'number' ? (data.LTCG * 100).toFixed(1) : data.LTCG;
    taxInfo.push(`LTCG: ${r}% (above ₹1L)`);
  }
  if (data.STCG) {
    const r = typeof data.STCG === 'number' ? (data.STCG * 100).toFixed(1) : data.STCG;
    taxInfo.push(`STCG: ${r === 'slab' ? 'at slab' : r + '%'}`);
  }
  if (data.Taxation) taxInfo.push(data.Taxation);
  return taxInfo.length > 0 ? taxInfo.join(' | ') : 'N/A';
};

const renderDetailValue = (value) => {
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  return value;
};

const makeDomId = (value) => value.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();

const getResultSummary = (results) => {
  const entries = Object.entries(results);
  if (!entries.length) return null;

  const byFinalValue = [...entries].sort(([, a], [, b]) => b.finalValue - a.finalValue);
  const byNetGain = [...entries].sort(([, a], [, b]) => (b.finalValue - b.tax - b.charges) - (a.finalValue - a.tax - a.charges));
  const avgFinalValue = entries.reduce((sum, [, value]) => sum + value.finalValue, 0) / entries.length;

  return {
    count: entries.length,
    best: byFinalValue[0],
    strongestNet: byNetGain[0],
    averageFinalValue: avgFinalValue,
  };
};

const getScenarioSummary = ({ principal, years, taxSlab, isSIP, sipDuration, inflationRate, applyExemptions, targetAmount }) => {
  const amount = principal ? fmt(Number(principal)) : 'an amount';
  const horizon = years ? `${years} year${Number(years) === 1 ? '' : 's'}` : 'a time horizon';
  const tax = taxSlab ? `${taxSlab}% tax slab` : 'your tax slab';
  const inflation = inflationRate ? `${inflationRate}% inflation` : 'no inflation adjustment';
  const target = targetAmount ? ` Goal target: ${fmt(Number(targetAmount))}.` : '';
  if (isSIP) {
    const duration = sipDuration ? `${sipDuration} yearly investments` : 'yearly investments';
    return `You are modelling ${amount} invested each year for ${duration}, held across ${horizon}, using ${tax} and ${inflation}.${target}`;
  }
  return `You are modelling ${amount} invested once for ${horizon}, using ${tax} and ${inflation}${applyExemptions ? ', with pre-tax income treatment where eligible' : ''}.${target}`;
};

const InvestmentCalculator = () => {
  const [principal, setPrincipal] = useState('');
  const [years, setYears] = useState('');
  const [taxSlab, setTaxSlab] = useState('');
  const [results, setResults] = useState({});
  const [sortOrder, setSortOrder] = useState(null);
  const [viewMode, setViewMode] = useState('default');
  const [applyExemptions, setApplyExemptions] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isSIP, setIsSIP] = useState(false);
  const [sipDuration, setSipDuration] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [inflationRate, setInflationRate] = useState('');
  const [setupMode, setSetupMode] = useState('fast');
  const [targetAmount, setTargetAmount] = useState('');

  const parsedYears = parseFloat(years);
  const parsedInflationRate = parseFloat(inflationRate);
  const parsedTargetAmount = parseFloat(targetAmount);
  const inflationEnabled = Number.isFinite(parsedInflationRate) && parsedInflationRate > 0;
  const targetEnabled = Number.isFinite(parsedTargetAmount) && parsedTargetAmount > 0;
  const scenarioSummary = getScenarioSummary({ principal, years, taxSlab, isSIP, sipDuration, inflationRate, applyExemptions, targetAmount });

  const handleCalculate = useCallback(() => {
    const p = parseFloat(principal);
    const y = parseFloat(years);
    const ts = parseFloat(taxSlab);
    const ir = parseFloat(inflationRate);
    if (isNaN(p) || isNaN(y) || isNaN(ts) || p <= 0 || y <= 0 || ts < 0 || ts > 100) {
      alert('Please enter valid positive numbers for principal, years, and tax slab (0-100).');
      return;
    }
    if (inflationRate !== '' && (isNaN(ir) || ir < 0 || ir > 100)) {
      alert('Please enter an inflation rate between 0 and 100.');
      return;
    }
    if (targetAmount !== '' && (isNaN(parsedTargetAmount) || parsedTargetAmount <= 0)) {
      alert('Please enter a valid positive goal target amount.');
      return;
    }
    if (isSIP) {
      const sd = parseFloat(sipDuration);
      if (isNaN(sd) || sd <= 0) {
        alert('Please enter a valid SIP duration (positive number of years).');
        return;
      }
      if (sd > y) {
        alert(`SIP Duration (${sd} years) must be ≤ Returns After (${y} years).`);
        return;
      }
    }
    const sd = isSIP ? parseFloat(sipDuration) : 0;
    setResults(calculateAllReturns(p, y, ts, investmentMetric, applyExemptions, '', isSIP, sd));
  }, [principal, years, taxSlab, inflationRate, parsedTargetAmount, targetAmount, applyExemptions, isSIP, sipDuration]);

  const applyScenarioPreset = (preset) => {
    setPrincipal(preset.values.principal);
    setYears(preset.values.years);
    setTaxSlab(preset.values.taxSlab);
    setInflationRate(preset.values.inflationRate);
    setIsSIP(preset.values.isSIP);
    setSipDuration(preset.values.sipDuration);
    setApplyExemptions(Boolean(preset.values.applyExemptions));
  };

  const toggleSort = useCallback(() => {
    setSortOrder(prev => {
      if (prev === 'asc') return 'desc';
      if (prev === 'desc') return null;
      return 'asc';
    });
    setViewMode('sorted');
  }, []);

  const setDefaultView = useCallback(() => {
    setViewMode('default');
    setSortOrder(null);
  }, []);

  const toggleExpand = (key) => setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleCategoryExpand = (cat) => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const groupResultsByCategory = (res) => {
    const grouped = {};
    Object.entries(res).forEach(([key, value]) => {
      const [category, ...rest] = key.split(' - ');
      if (!grouped[category]) grouped[category] = {};
      grouped[category][rest.join(' - ')] = value;
    });
    return grouped;
  };

  const sortResults = (res) => {
    if (!sortOrder) return res;
    return Object.fromEntries(
      Object.entries(res).sort(([, a], [, b]) =>
        sortOrder === 'desc' ? b.finalValue - a.finalValue : a.finalValue - b.finalValue
      )
    );
  };

  const filterResults = (res) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return res;
    return Object.fromEntries(
      Object.entries(res).filter(([key]) => key.toLowerCase().includes(query))
    );
  };

  const getOriginalInvestmentData = (key) => {
    const parts = key.split(' - ');
    let data = investmentMetric;
    for (const part of parts) {
      if (data[part]) data = data[part];
      else return {};
    }
    return data;
  };

  const getInvestmentDetails = (key) => {
    const parts = key.split(' - ');
    let details = investmentJson;
    if (!details[parts[0]].hasOwnProperty('subCategories')) return details[parts[0]];
    for (const part of parts) {
      if (details.subCategories) details = details.subCategories[part] || {};
      else details = details[part] || {};
    }
    return details;
  };

  // Handler factories
  const makeRateHandler = (field) => (key, val) => {
    const v = parseFloat(val);
    if (!isNaN(v)) setResults(prev => ({ ...prev, [key]: { ...prev[key], [field]: v / 100 } }));
  };

  const handleInterestRateChange = makeRateHandler('interestRate');
  const handleChargesChange = makeRateHandler('chargesRate');
  const handlePerformanceFeeChange = makeRateHandler('performanceFee');
  const handlePerformanceTargetChange = makeRateHandler('performanceTarget');
  const handleRentalYieldChange = makeRateHandler('rentalYield');
  const handleMaintenanceChange = makeRateHandler('maintenanceRate');
  const handleDepreciationChange = makeRateHandler('depreciationRate');

  const handleRecalculate = (key) => {
    const originalData = getOriginalInvestmentData(key);
    const updatedData = {
      ...originalData,
      Returns: results[key].interestRate,
      Charges: results[key].chargesRate,
    };
    if (originalData.performanceFee !== undefined) updatedData.performanceFee = results[key].performanceFee;
    if (originalData.performanceTarget !== undefined) updatedData.performanceTarget = results[key].performanceTarget;
    if (originalData.RentalYield !== undefined) updatedData.RentalYield = results[key].rentalYield;
    if (originalData.MaintenanceRate !== undefined) updatedData.MaintenanceRate = results[key].maintenanceRate;
    if (originalData.DepreciationRate !== undefined) updatedData.DepreciationRate = results[key].depreciationRate;

    let updatedResult;
    if (isSIP && updatedData.SIPEligible) {
      updatedResult = calculateSIPReturns(parseFloat(principal), parseFloat(years), parseFloat(sipDuration), updatedData, parseFloat(taxSlab), applyExemptions);
    } else {
      updatedResult = calculateReturns(parseFloat(principal), parseFloat(years), updatedData, parseFloat(taxSlab), applyExemptions);
    }

    const preserved = {
      interestRate: results[key].interestRate,
      chargesRate: results[key].chargesRate,
      performanceFee: results[key].performanceFee,
      performanceTarget: results[key].performanceTarget,
      rentalYield: results[key].rentalYield,
      maintenanceRate: results[key].maintenanceRate,
      depreciationRate: results[key].depreciationRate,
    };
    setResults(prev => ({ ...prev, [key]: { ...updatedResult, ...preserved } }));
  };

  const renderResultItem = (category, key, value) => {
    const fullKey = `${category} - ${key}`;
    const itemId = makeDomId(fullKey);
    const investmentDetails = getInvestmentDetails(fullKey);
    const originalData = getOriginalInvestmentData(fullKey);
    const isSIPDisabled = isSIP && !value.isSIP;
    const realFinalValue = getInflationAdjustedValue(value.finalValue, parsedYears, parsedInflationRate);

    return (
      <article
        key={fullKey}
        className={`invcalc-item ${value.isSIP ? 'invcalc-item--sip' : ''} ${isSIPDisabled ? 'invcalc-item--disabled' : ''}`}
      >
        <div className="invcalc-item-header">
          <div>
            <p className="invcalc-item-kicker">{category}</p>
            <h3>{key}</h3>
          </div>
          <span>
            {value.isSIP && (
              <span className="invcalc-badge invcalc-badge--sip">SIP ({value.sipDuration}yr)</span>
            )}
            {isSIPDisabled && (
              <span className="invcalc-badge invcalc-badge--nosip">Not SIP-eligible</span>
            )}
          </span>
        </div>

        <div className="invcalc-item-body">
          {value.isSIP && (
            <div className="invcalc-sip-total">
              <strong>Total Invested:</strong> {fmt(value.totalInvested)} ({fmt(value.totalInvested / value.sipDuration)} × {value.sipDuration}yr)
            </div>
          )}

          {/* Rate adjustment row */}
          <div className="invcalc-rates-row">
            <div className="invcalc-rate-field">
              <label htmlFor={`${itemId}-rate`}>Interest Rate (%):</label>
              <input
                id={`${itemId}-rate`}
                type="text"
                value={(value.interestRate * 100).toFixed(2)}
                onChange={(e) => handleInterestRateChange(fullKey, e.target.value)}
                className="invcalc-rate-input"
              />
            </div>
            <div className="invcalc-rate-field">
              <label htmlFor={`${itemId}-charges`}>Charges (%):</label>
              <input
                id={`${itemId}-charges`}
                type="text"
                value={(value.chargesRate * 100).toFixed(2)}
                onChange={(e) => handleChargesChange(fullKey, e.target.value)}
                className="invcalc-rate-input"
              />
            </div>
            {originalData.performanceFee !== undefined && (
              <div className="invcalc-rate-field">
                <label htmlFor={`${itemId}-performance-fee`}>Performance Fee (%):</label>
                <input id={`${itemId}-performance-fee`} type="text" value={(value.performanceFee * 100).toFixed(2)} onChange={(e) => handlePerformanceFeeChange(fullKey, e.target.value)} className="invcalc-rate-input" />
              </div>
            )}
            {originalData.performanceTarget !== undefined && (
              <div className="invcalc-rate-field">
                <label htmlFor={`${itemId}-performance-target`}>Performance Target (%):</label>
                <input id={`${itemId}-performance-target`} type="text" value={(value.performanceTarget * 100).toFixed(2)} onChange={(e) => handlePerformanceTargetChange(fullKey, e.target.value)} className="invcalc-rate-input" />
              </div>
            )}
            {originalData.RentalYield !== undefined && (
              <div className="invcalc-rate-field">
                <label htmlFor={`${itemId}-rental-yield`}>Rental Yield (%):</label>
                <input id={`${itemId}-rental-yield`} type="text" value={(value.rentalYield * 100).toFixed(2)} onChange={(e) => handleRentalYieldChange(fullKey, e.target.value)} className="invcalc-rate-input" />
              </div>
            )}
            {originalData.MaintenanceRate !== undefined && (
              <div className="invcalc-rate-field">
                <label htmlFor={`${itemId}-maintenance`}>Maint. (%):</label>
                <input id={`${itemId}-maintenance`} type="text" value={(value.maintenanceRate * 100).toFixed(2)} onChange={(e) => handleMaintenanceChange(fullKey, e.target.value)} className="invcalc-rate-input" />
              </div>
            )}
            {originalData.DepreciationRate !== undefined && (
              <div className="invcalc-rate-field">
                <label htmlFor={`${itemId}-depreciation`}>Depr. (%):</label>
                <input id={`${itemId}-depreciation`} type="text" value={(value.depreciationRate * 100).toFixed(2)} onChange={(e) => handleDepreciationChange(fullKey, e.target.value)} className="invcalc-rate-input" />
              </div>
            )}
            <button className="clean-btn clean-btn--ghost" onClick={() => handleRecalculate(fullKey)} style={{ fontSize: '11px' }} title="Recalculate this card after editing its return, charge, tax, or property assumptions.">
              Recalculate
            </button>
          </div>

          {/* Summary */}
          <div className="invcalc-summary">
            <div className="invcalc-summary-row">
              <span className="invcalc-summary-label">Interest earned</span>
              <span className="invcalc-summary-val">{fmt(value.interest)}</span>
            </div>
            <div className="invcalc-summary-row">
              <span className="invcalc-summary-label">Tax payable</span>
              <span className="invcalc-summary-val">{fmt(value.tax)}</span>
            </div>
            <div className="invcalc-summary-row">
              <span className="invcalc-summary-label">Charges</span>
              <span className="invcalc-summary-val">{fmt(value.charges)}</span>
            </div>
            {value.performanceFees > 0 && (
              <div className="invcalc-summary-row">
                <span className="invcalc-summary-label">Perf. fees</span>
                <span className="invcalc-summary-val">{fmt(value.performanceFees)}</span>
              </div>
            )}
            <div className="invcalc-summary-row">
              <span className="invcalc-summary-label">Final Value</span>
              <span className="invcalc-summary-val">{fmt(value.finalValue)}</span>
            </div>
            {inflationEnabled && (
              <div className="invcalc-summary-row invcalc-summary-row--real">
                <span className="invcalc-summary-label">Real Final Value</span>
                <span className="invcalc-summary-val">{fmt(Math.round(realFinalValue))}</span>
              </div>
            )}
          </div>

          <div className="invcalc-assumption-receipt">
            <strong>Assumptions used:</strong> {(value.interestRate * 100).toFixed(2)}% return,
            {(value.chargesRate * 100).toFixed(2)}% charges, {renderTaxationInfo(originalData)}
            {value.isSIP ? `, yearly SIP for ${value.sipDuration} years` : ', one-time investment'}
            {inflationEnabled ? `, ${parsedInflationRate}% inflation for real value` : ''}.
          </div>

          {/* Notes */}
          {value.stcgLtcgMix && (
            <p className="invcalc-note">(Assuming 65% LTCG / 35% STCG for tax)</p>
          )}
          {value.performanceFee > 0 && (
            <p className="invcalc-note">(Perf. fee: {(value.performanceFee * 100).toFixed(2)}% above {(value.performanceTarget * 100).toFixed(2)}% target)</p>
          )}
          {(value.rentalYield > 0 || value.maintenanceRate > 0) && (
            <p className="invcalc-note">(Rental: {(value.rentalYield * 100).toFixed(2)}% | Maint: {(value.maintenanceRate * 100).toFixed(2)}% | Depr: {(value.depreciationRate * 100).toFixed(2)}%)</p>
          )}

          {value.assetValue !== undefined && (originalData.RentalYield !== undefined || originalData.DepreciationRate !== undefined) && (
            <div className="invcalc-breakdown">
              <div><strong>Final Asset Value:</strong> {fmt(value.assetValue)}</div>
              <div><strong>Total Net Rent (Cash):</strong> {fmt(value.accumulatedCash)}</div>
            </div>
          )}

          <div className="invcalc-tax-info">
            <strong>Taxation:</strong> {renderTaxationInfo(originalData)}<br />
            <strong>Charges:</strong> {renderDetailValue(investmentDetails['Charges and Expenses']) || 'N/A'}
          </div>

          <button
            className="clean-btn clean-btn--ghost"
            onClick={() => toggleExpand(fullKey)}
            style={{ fontSize: '11px', marginTop: '4px' }}
            title="Show or hide the reference details behind this investment option."
          >
            {expandedItems[fullKey] ? 'Less' : 'More'}
          </button>

          {expandedItems[fullKey] && (
            <div className="invcalc-details">
              {Object.entries(investmentDetails).map(([dk, dv]) => (
                <div key={dk}><strong>{dk}:</strong> {renderDetailValue(dv)}</div>
              ))}
            </div>
          )}
        </div>
      </article>
    );
  };

  const renderResults = () => {
    const sortedResults = sortResults(filterResults(results));
    if (viewMode === 'default') {
      const grouped = groupResultsByCategory(sortedResults);
      return Object.entries(grouped).map(([category, items]) => (
        <div key={category} className="invcalc-category">
          <h2 className="invcalc-category-header" onClick={() => toggleCategoryExpand(category)}>
            <button type="button">
              <span aria-hidden="true">{expandedCategories[category] ? '▾' : '▸'}</span>
              <span>{category}</span>
            </button>
          </h2>
          {expandedCategories[category] && (
            <div className="invcalc-category-body">
              {Object.entries(items).map(([key, value]) => renderResultItem(category, key, value))}
            </div>
          )}
        </div>
      ));
    }
    return Object.entries(sortedResults).map(([key, value]) => {
      const [category, ...rest] = key.split(' - ');
      return (
        <div key={key} className="invcalc-flat-item">
          {renderResultItem(category, rest.join(' - '), value)}
        </div>
      );
    });
  };

  const hasResults = Object.keys(results).length > 0;
  const summary = getResultSummary(results);
  const visibleResultCount = Object.keys(filterResults(results)).length;
  const bestRealValue = summary
    ? getInflationAdjustedValue(summary.best[1].finalValue, parsedYears, parsedInflationRate)
    : null;
  const goalValueForBest = summary
    ? (inflationEnabled ? bestRealValue : summary.best[1].finalValue)
    : null;
  const goalProgress = targetEnabled && goalValueForBest
    ? Math.min((goalValueForBest / parsedTargetAmount) * 100, 999)
    : null;
  const goalGap = targetEnabled && goalValueForBest
    ? parsedTargetAmount - goalValueForBest
    : null;

  return (
    <PageLayout
      title="Investment Calculator"
      subtitle="Compare Indian investment routes after estimated taxes, charges, rent, and SIP timing assumptions."
      statusText={hasResults ? `${Object.keys(results).length} investment types calculated` : 'Enter parameters to calculate'}
    >
      <div className="invcalc-page">
        <div className="invcalc-disclaimer">
          <strong>Important:</strong> This is an estimate, not financial advice. Assumptions are editable per instrument because tax rules,
          yields, charges, and personal circumstances can change the answer materially.
        </div>

        <section className="invcalc-form" aria-labelledby="scenario-title">
          <div className="invcalc-form-header">
            <div>
              <p className="invcalc-eyebrow">Scenario builder</p>
              <h2 id="scenario-title">Model your investment horizon</h2>
            </div>
            <span className="invcalc-form-chip">{isSIP ? 'Yearly SIP' : 'One-time investment'}</span>
          </div>

          <div className="invcalc-mode-toggle" role="group" aria-label="Setup mode">
            <button
              type="button"
              className={`invcalc-mode-btn ${setupMode === 'fast' ? 'invcalc-mode-btn--active' : ''}`}
              onClick={() => setSetupMode('fast')}
              title="Shows the core inputs without example presets."
            >
              Fast setup
            </button>
            <button
              type="button"
              className={`invcalc-mode-btn ${setupMode === 'guided' ? 'invcalc-mode-btn--active' : ''}`}
              onClick={() => setSetupMode('guided')}
              title="Shows example scenarios and extra setup guidance."
            >
              Guided setup
            </button>
          </div>

          {setupMode === 'guided' && (
            <div className="invcalc-guided-panel">
              <div>
                <strong>Start with an example</strong>
                <p>Use these presets as a starting point, then adjust the fields.</p>
              </div>
              <div className="invcalc-scenario-chips">
                {SCENARIO_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    className="invcalc-scenario-chip"
                    onClick={() => applyScenarioPreset(preset)}
                    title={preset.description}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="invcalc-exemption-label">
            <input
              type="checkbox"
              checked={applyExemptions}
              onChange={(e) => setApplyExemptions(e.target.checked)}
              title="Treat eligible investments as funded from pre-tax income where tax deduction rules apply."
            />
            Calculate for pre-tax income (apply 80C exemption where applicable, slab tax on others)
          </label>

          <div className="invcalc-form-grid">
            <div className="clean-field">
              <label className="clean-label" htmlFor="principal">Principal Amount (₹)</label>
              <input id="principal" type="number" value={principal} onChange={(e) => setPrincipal(e.target.value)} className="clean-input" placeholder="e.g. 100000" />
              <p className="invcalc-field-help">Amount invested once. In SIP mode, this is the amount invested each year.</p>
            </div>
            <div className="clean-field">
              <label className="clean-label" htmlFor="years">Returns After (Years)</label>
              <input id="years" type="number" value={years} onChange={(e) => setYears(e.target.value)} className="clean-input" placeholder="e.g. 10" />
              <p className="invcalc-field-help">How long the money remains invested before comparing final values.</p>
            </div>
            <div className="clean-field">
              <label className="clean-label" htmlFor="tax-slab">Your Tax Slab (%)</label>
              <input id="tax-slab" type="number" value={taxSlab} onChange={(e) => setTaxSlab(e.target.value)} className="clean-input" placeholder="e.g. 30" />
              <p className="invcalc-field-help">Used for taxable interest/gains estimates where slab taxation applies.</p>
            </div>
            <div className="clean-field">
              <label className="clean-label" htmlFor="inflation-rate">Inflation (%)</label>
              <input id="inflation-rate" type="number" value={inflationRate} onChange={(e) => setInflationRate(e.target.value)} className="clean-input" placeholder="e.g. 6" />
              <p className="invcalc-field-help">Optional. Shows future values in today's purchasing power.</p>
            </div>
            <div className="clean-field">
              <label className="clean-label" htmlFor="target-amount">Goal Target (₹, optional)</label>
              <input id="target-amount" type="number" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} className="clean-input" placeholder="e.g. 1000000" />
              <p className="invcalc-field-help">Goal mode lite. Compares the best result against the amount you want to reach.</p>
            </div>
          </div>

          <div className="invcalc-sip-panel">
            <label className="invcalc-sip-toggle">
              <input type="checkbox" checked={isSIP} onChange={(e) => setIsSIP(e.target.checked)} title="Model repeated yearly investments instead of a single investment." />
              <strong>SIP Mode</strong> — invest the principal amount yearly for multiple years
            </label>
            {isSIP && (
              <div className="invcalc-sip-detail">
                <div className="clean-field">
                  <label className="clean-label" htmlFor="sip-duration">Duration of SIP (Years, must be ≤ Returns After)</label>
                  <input
                    id="sip-duration"
                    type="number"
                    value={sipDuration}
                    onChange={(e) => setSipDuration(e.target.value)}
                    placeholder={`Max: ${years || '?'}`}
                    className="clean-input"
                  />
                </div>
                <p className="invcalc-sip-note">
                  SIP Duration is how many yearly investments you make. First installment grows for the full period; later ones grow for less time.
                </p>
              </div>
            )}
          </div>

          <div className="invcalc-scenario-summary" aria-label="Scenario summary">
            {scenarioSummary}
          </div>

          <details className="invcalc-help-details">
            <summary>What do these controls mean?</summary>
            <dl>
              <div><dt>Principal</dt><dd>Single invested amount, or yearly amount when SIP mode is on.</dd></div>
              <div><dt>Returns After</dt><dd>How long each option is held before comparing final value.</dd></div>
              <div><dt>Tax Slab</dt><dd>Used when an option is taxed as regular income.</dd></div>
              <div><dt>Goal Target</dt><dd>Optional target amount for the goal progress panel after calculation.</dd></div>
            </dl>
          </details>

          <button className="clean-btn clean-btn--primary" onClick={handleCalculate} title="Calculate every investment type using the current assumptions.">
            Calculate All
          </button>
        </section>

        {hasResults && (
          <div className="invcalc-results">
            <h2 className="invcalc-results-title">Results</h2>
            {summary && (
              <section className="invcalc-summary-strip" aria-label="Result summary">
                <div>
                  <span>{inflationEnabled ? 'Best real value' : 'Best final value'}</span>
                  <strong>{summary.best[0]}</strong>
                  <em>{fmt(Math.round(inflationEnabled ? bestRealValue : summary.best[1].finalValue))}</em>
                </div>
                <div>
                  <span>Strongest net outcome</span>
                  <strong>{summary.strongestNet[0]}</strong>
                  <em>{fmt(summary.strongestNet[1].finalValue)}</em>
                </div>
                <div>
                  <span>Average final value</span>
                  <strong>{fmt(summary.averageFinalValue)}</strong>
                  <em>{summary.count} options compared</em>
                </div>
              </section>
            )}

            <div className="invcalc-assumptions-panel">
              <strong>Assumptions used</strong>
              <span>{isSIP ? `Yearly SIP for ${sipDuration || 0} years` : 'One-time investment'}</span>
              <span>{taxSlab}% tax slab</span>
              <span>{applyExemptions ? 'Pre-tax income treatment enabled' : 'Post-tax investment amount'}</span>
              <span>{inflationEnabled ? `${parsedInflationRate}% inflation shown as real value` : 'No inflation adjustment'}</span>
              {targetEnabled && <span>{fmt(parsedTargetAmount)} goal target</span>}
            </div>

            {targetEnabled && summary && (
              <section className="invcalc-goal-panel" aria-label="Goal mode lite">
                <div>
                  <span>Goal mode lite</span>
                  <strong>{goalProgress.toFixed(0)}% of target</strong>
                  <p>
                    Best current match is {summary.best[0]} at {fmt(Math.round(goalValueForBest))}{inflationEnabled ? ' in real value' : ''}.
                  </p>
                </div>
                <div className="invcalc-goal-meter" aria-hidden="true">
                  <span style={{ width: `${Math.min(goalProgress, 100)}%` }} />
                </div>
                <p className={goalGap <= 0 ? 'invcalc-goal-status invcalc-goal-status--met' : 'invcalc-goal-status'}>
                  {goalGap <= 0
                    ? `Target cleared by ${fmt(Math.round(Math.abs(goalGap)))}.`
                    : `Short by ${fmt(Math.round(goalGap))}. Try more principal, longer horizon, SIP mode, or lower return drag.`}
                </p>
              </section>
            )}

            <div className="invcalc-results-toolbar">
              <label className="invcalc-search">
                <span>Filter investments</span>
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search PF, gold, real estate..."
                  className="clean-input"
                />
              </label>
              <button className="clean-btn clean-btn--secondary" onClick={toggleSort} title="Cycle between ascending, descending, and grouped result order.">
                Sort by Final Value {sortOrder === 'asc' ? 'ascending' : sortOrder === 'desc' ? 'descending' : ''}
              </button>
              <button
                className={`clean-btn ${viewMode === 'default' ? 'clean-btn--primary' : 'clean-btn--secondary'}`}
                onClick={setDefaultView}
                title="Group results by investment category."
              >
                Grouped View
              </button>
              <span style={{ fontSize: '12px', color: 'var(--c-text-muted)' }}>
                {visibleResultCount} of {Object.keys(results).length} results
              </span>
            </div>
            {renderResults()}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default InvestmentCalculator;
